import { useState, useEffect, useCallback } from 'react';
import subscriptionService from '../services/subscriptionService';
import { processSubscriptionsData } from '../utils/subscriptionUtils';
import { UX_TIMING, OPERATION_STATUS } from '../constants';

/**
 * Hook personalizado para gestión de suscripciones
 */
export const useSubscriptions = () => {
  // Estados principales
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expiring: 0, inactive: 0 });
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de paginación - NUEVO
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Estados de operaciones
  const [operationStatus, setOperationStatus] = useState(null);
  const [operatingSubscriptionName, setOperatingSubscriptionName] = useState('');
  const [isEditingMode, setIsEditingMode] = useState(false);

  /**
   * Cargar todas las suscripciones con paginación
   */
  const loadSubscriptions = useCallback(async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      setError(null);

      // Simular tiempo mínimo de carga para UX
      const startTime = Date.now();

      const [subscriptionsResult, statsResult, contactsResult] = await Promise.all([
        subscriptionService.getAllSubscriptions({ page, limit }), // Pasar parámetros de paginación
        subscriptionService.getSubscriptionStats(),
        subscriptionService.getAvailableContacts(),
      ]);

      // Procesar suscripciones
      if (subscriptionsResult.success) {
        // Las suscripciones están en data.subscriptions en la nueva estructura del backend
        const subscriptionsData = subscriptionsResult.data?.subscriptions || [];
        const processedSubscriptions = processSubscriptionsData(subscriptionsData);
        setSubscriptions(processedSubscriptions);

        // Capturar información de paginación del backend
        if (subscriptionsResult.data?.pagination) {
          setPagination(subscriptionsResult.data.pagination);
        }
      } else {
        throw new Error(subscriptionsResult.message);
      }

      // Cargar estadísticas
      if (statsResult.success) {
        // Las estadísticas pueden estar directamente en data o en data.stats
        const statsData = statsResult.data?.stats || statsResult.data || {};
        setStats(statsData);
      }

      // Cargar contactos disponibles
      if (contactsResult.success) {
        // Los contactos pueden estar en data.contacts o directamente en data
        const contactsData = contactsResult.data?.contacts || contactsResult.data || [];
        setAvailableContacts(contactsData);
      }

      // Asegurar tiempo mínimo de loading para UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

    } catch (error) {
      console.error('Error cargando suscripciones:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambiar a una página específica
   */
  const goToPage = useCallback(async (page) => {
    await loadSubscriptions(page, pagination.limit);
  }, [loadSubscriptions, pagination.limit]);

  /**
   * Ir a la página siguiente
   */
  const nextPage = useCallback(async () => {
    if (pagination.hasNext) {
      await goToPage(pagination.page + 1);
    }
  }, [pagination.hasNext, pagination.page, goToPage]);

  /**
   * Ir a la página anterior
   */
  const prevPage = useCallback(async () => {
    if (pagination.hasPrev) {
      await goToPage(pagination.page - 1);
    }
  }, [pagination.hasPrev, pagination.page, goToPage]);

  /**
   * Cambiar el número de elementos por página
   */
  const changePageSize = useCallback(async (newLimit) => {
    await loadSubscriptions(1, newLimit); // Reiniciar a página 1 con nuevo tamaño
  }, [loadSubscriptions]);

  /**
   * Ejecuta una operación de suscripción con UX optimizada
   */
  const executeSubscriptionOperation = useCallback(async (operation, subscriptionData, isEdit = false) => {
    setOperatingSubscriptionName(subscriptionData.contact_name || 'suscripción');
    setOperationStatus(OPERATION_STATUS.LOADING);
    setIsEditingMode(isEdit);

    const startTime = Date.now();

    try {
      const response = await operation();

      // Asegurar tiempo mínimo de loading para UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.success) {
        setOperationStatus(OPERATION_STATUS.SUCCESS);
        await loadSubscriptions(); // Recargar lista
        return { success: true };
      } else {
        setOperationStatus(OPERATION_STATUS.ERROR);
        return { success: false, message: response.message };
      }

    } catch (error) {
      console.error('Error en operación de suscripción:', error);
      setOperationStatus(OPERATION_STATUS.ERROR);
      return { success: false, message: error.message };
    }
  }, [loadSubscriptions]);

  /**
   * Crear nueva suscripción
   */
  const createSubscription = useCallback(async (subscriptionData) => {
    return executeSubscriptionOperation(
      () => subscriptionService.createSubscription(subscriptionData),
      subscriptionData,
      false
    );
  }, [executeSubscriptionOperation]);

  /**
   * Actualizar suscripción existente
   */
  const updateSubscription = useCallback(async (subscriptionId, subscriptionData) => {
    return executeSubscriptionOperation(
      () => subscriptionService.updateSubscription(subscriptionId, subscriptionData),
      subscriptionData,
      true
    );
  }, [executeSubscriptionOperation]);

  /**
   * Eliminar suscripción
   */
  const deleteSubscription = useCallback(async (subscriptionId, subscriptionData = { contact_name: 'Suscripción' }) => {
    return executeSubscriptionOperation(
      () => subscriptionService.deleteSubscription(subscriptionId),
      subscriptionData,
      false
    );
  }, [executeSubscriptionOperation]);

  /**
   * Resetear estado de operaciones
   */
  const resetOperationState = useCallback(() => {
    setOperationStatus(null);
    setOperatingSubscriptionName('');
    setIsEditingMode(false);
  }, []);

  /**
   * Obtener suscripción por ID
   */
  const getSubscriptionById = useCallback(async (subscriptionId) => {
    try {
      const result = await subscriptionService.getSubscriptionById(subscriptionId);
      
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error obteniendo suscripción:', error);
      return { success: false, message: error.message };
    }
  }, []);

  /**
   * Refrescar contactos disponibles
   */
  const refreshAvailableContacts = useCallback(async () => {
    try {
      const result = await subscriptionService.getAvailableContacts();
      
      if (result.success) {
        setAvailableContacts(result.data);
      }
    } catch (error) {
      console.error('Error refrescando contactos disponibles:', error);
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  return {
    // Estados principales
    subscriptions,
    stats,
    availableContacts,
    loading,
    error,
    pagination, // Exponer el estado de paginación

    // Estados de operaciones
    operationStatus,
    operatingSubscriptionName,
    isEditingMode,

    // Funciones
    createSubscription,
    updateSubscription,
    deleteSubscription,
    getSubscriptionById,
    refreshAvailableContacts,
    resetOperationState,
    loadSubscriptions,
    goToPage, // Exponer funciones de paginación
    nextPage,
    prevPage,
    changePageSize,
  };
}; 